import { err, ok, type Result } from "./result.ts";
import { DateModel } from "$models";
import { type DateFormat, formatDate } from "../utils/dateTime.ts";

type Decoder<a> = (s: string) => Result<a, string>;

type Encodable = string | number | null | undefined;

// Splits the url into a base and a fake query url
// "fake query url" i define as the
function splitUrl(): { base: string; fakeQuery: string } {
  const url = globalThis.location.href;
  const spaPath = url.split("#")[1];
  if (spaPath === undefined) {
    return { base: url, fakeQuery: "" };
  }

  const fakeQuery = spaPath.split("?")[1];
  if (fakeQuery === undefined) {
    return { base: url, fakeQuery: "" };
  }

  const idx = url.lastIndexOf(fakeQuery);

  return {
    base: url.slice(0, idx - 1),
    fakeQuery,
  };
}
// deno-lint-ignore no-namespace
export namespace Encode {
  export const setUrlQuery = <a>(obj: { [p in keyof a]: Encodable }): void => {
    const newQry = asQuery(obj);
    const { base: existing } = splitUrl();
    globalThis.history.replaceState(null, "", existing + "?" + newQry);
  };

  export const updateUrlQuery = <a>(
    obj: { [p in keyof a]: Encodable },
  ): void => {
    const { base: existing, fakeQuery } = splitUrl();
    const currentObject = Decode.object(fakeQuery, {}).unwrapOr(
      {},
    ) as unknown as object;
    const newQry = asQuery({ ...obj, ...currentObject });
    globalThis.history.replaceState(null, "", existing + "?" + newQry);
  };

  export const asQuery = <a>(
    obj: { [p in keyof a]: Encodable },
  ): string => {
    let newQry = "";
    for (const key in obj) {
      const val = encodeURI(obj[key]?.toString() ?? "");
      if (val) {
        newQry = newQry + key + "=" + val + "&";
      }
    }

    if (0 < newQry.length) {
      newQry = newQry.slice(0, -1);
    }
    return newQry;
  };
}

// deno-lint-ignore no-namespace
export namespace Decode {
  export function query<a>(
    decoders: { [p in keyof a]: Decoder<a[p]> },
    useStrict?: boolean,
  ): Result<a, string> {
    return object<a>(
      globalThis.location.href.split("#").pop()?.split("?").pop() ?? "",
      decoders,
      useStrict,
    );
  }

  export const date: (f: DateFormat) => Decoder<DateModel> =
    (format: DateFormat) =>
    (
      s: string,
    ): Result<DateModel, string> => {
      const date = formatDate(s, format);
      if (date === "") {
        return err("Not a date");
      }

      return ok(new DateModel(date));
    };
  export const bool: Decoder<boolean> = (
    s: string,
  ): Result<boolean, string> => {
    if (!s) {
      return err("missing");
    }

    try {
      const obj = JSON.parse(s.trim());
      if (typeof obj !== "boolean") {
        return err("Not a boolean");
      }
      return ok(obj as boolean);
    } catch (error) {
      return err(`Unable to parse bool: ` + JSON.stringify(error));
    }
  };
  export const integer: Decoder<number> = (s: string) => {
    if (!s) {
      return err("missing");
    }
    const res = parseInt(s);
    if (Number.isNaN(res)) {
      return err(`attempted to parse '${s}' as a number`);
    }
    return ok(res);
  };

  export function array<T>(s: string): Result<T[], string> {
    if (!s) {
      return err("Missing");
    }

    try {
      const obj = JSON.parse(decodeURI(s));
      return ok(obj as T[]);
    } catch {
      return err("Unable to parse JSON object");
    }
  }

  export const text: Decoder<string> = (s: string) =>
    s ? ok(decodeURI(s)) : err("missing");

  export function literal<T>(value: T): Decoder<T> {
    return (s: string) =>
      s === value ? ok<T, string>(s as T) : err<T, string>("missing");
  }

  export const csvStringArray: Decoder<string[]> = (s: string) => {
    const res = s.split(",");
    if (res.length === 0) {
      return err("missing csv values");
    } else {
      return ok(res);
    }
  };

  export function optional<a>(dec: Decoder<a>): Decoder<a | undefined> {
    return (s: string) => {
      if (s) {
        return dec(s);
      } else {
        return ok(undefined);
      }
    };
  }

  export function exists_or<a>(dec: Decoder<a>, default_value: a): Decoder<a> {
    return (s: string) => {
      if (s) {
        return dec(s);
      } else {
        return ok(default_value);
      }
    };
  }

  export function enumeration<a>(
    enumObj: object,
  ): Decoder<a> {
    return (s: string) => {
      const enumValue = Object.values(enumObj).find((x: any) => x === s);
      if (enumValue !== undefined) {
        return ok(enumValue);
      }
      return err(`value '${s}' matches no variant in enum ${enumObj}`);
    };
  }

  export function object<a>(
    s: string,
    decoders: { [p in keyof a]: Decoder<a[p]> },
    strict?: boolean,
  ): Result<a, string> {
    const kvs: any = {};
    s.split("&").forEach((s) => {
      const [key, val] = s.split("=");
      kvs[key] = val;
    });

    for (const key in kvs) {
      if (!decoders.hasOwnProperty(key)) {
        if (!strict) {
          console.debug(`ignoring '${key}=${kvs[key]}'`);
        } else {
          return err(`uhandled param '${key}`);
        }
      }
    }

    const result: any = {};
    for (const key in decoders) {
      if (decoders.hasOwnProperty(key)) {
        const r = decoders[key](kvs[key]);
        if (r.isOk()) {
          if (r.value) { // dont set if was undefined
            result[key] = r.value;
          }
        } else {
          return err(`param '${key}': ${r.error}`);
        }
      }
    }

    return ok<a, "string">(result);
  }
}
